import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { getLang, getLocale } from "../lang/useLanguage";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10); };
const dayName = (d) => new Date(d).toLocaleDateString(getLocale(), { weekday: "short" });

const SHIFT_CONFIG = {
  "Gunduz": { label: "Gündüz", color: "#fbbf24", bg: "rgba(251,191,36,0.15)", icon: "☀️", hours: "08:00-16:00" },
  "Aksam":  { label: "Akşam",  color: "#60a5fa", bg: "rgba(96,165,250,0.15)",  icon: "🌆", hours: "16:00-24:00" },
  "Gece":   { label: "Gece",   color: "#a78bfa", bg: "rgba(167,139,250,0.15)", icon: "🌙", hours: "00:00-08:00" },
};

const ATTEND_STATUS = {
  present: { label: "Geldi",   color: "#00ff9d" },
  absent:  { label: "Gelmedi", color: "#f87171" },
  late:    { label: "Geç",     color: "#fbbf24" },
  leave:   { label: "İzinli",  color: "#60a5fa" },
};

const getShiftConfigLabel = (key) => {
  const h = getLang().hr || {};

  const map = {
    Gunduz: h.shifts?.day,
    Aksam: h.shifts?.evening,
    Gece: h.shifts?.night,
  };

  return map[key] || SHIFT_CONFIG[key]?.label || key || "—";
};

const getAttendStatusLabel = (key) => {
  const h = getLang().hr || {};
  return h.attendanceStatus?.[key] || ATTEND_STATUS[key]?.label || key || "—";
};

const getRoleLabel = (value) => {
  const h = getLang().hr || {};
  return h.roles?.[value] || value || "—";
};

const AssignModal = ({ personnel, machines, onClose, onSaved, t }) => {
  const [form, setForm] = useState({
    personnel_id: personnel[0]?.id || "",
    machine_id: machines[0]?.machine_id || "",
    shift_name: "Gunduz",
    shift_date: today(),
    start_time: "08:00",
    end_time: "16:00",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleShiftChange = (shift) => {
    const cfg = SHIFT_CONFIG[shift];
    const [start, end] = cfg.hours.split("-");
    set("shift_name", shift);
    set("start_time", start);
    set("end_time", end);
  };

  const handleSave = async () => {
    if (!form.personnel_id || !form.machine_id) { setError(getLang().hr?.shiftsUi?.personnelMachineRequired); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/shifts/assign`, {
        method: "POST", headers: headers(), body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || getLang().hr?.shiftsUi?.saveError);
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px",
    background: t.bg.input, border: `1px solid ${t.border.input}`,
    borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay,
      backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`,
        borderRadius: 20, width: "100%", maxWidth: 520,
        overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>{getLang().hr?.shiftsUi?.assignShiftTitle}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
              textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{getLang().hr?.shiftsUi?.shift}</label>
            <div style={{ display: "flex", gap: 8 }}>
              {Object.entries(SHIFT_CONFIG).map(([key, cfg]) => (
                <button key={key} onClick={() => handleShiftChange(key)} style={{
                  flex: 1, padding: "10px 0", borderRadius: 10, cursor: "pointer",
                  border: `2px solid ${form.shift_name === key ? cfg.color : t.border.input}`,
                  background: form.shift_name === key ? cfg.bg : t.bg.input,
                  color: form.shift_name === key ? cfg.color : t.text.muted,
                  fontSize: 12, fontWeight: 700, transition: "all 0.15s",
                }}>
                  <div>{cfg.icon} {getShiftConfigLabel(key)}</div>
                  <div style={{ fontSize: 10, marginTop: 2 }}>{cfg.hours}</div>
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{getLang().hr?.shiftsUi?.personnel}</label>
              <select value={form.personnel_id} onChange={e => set("personnel_id", e.target.value)} style={inputStyle}>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.name} {p.surname}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Makine</label>
              <select value={form.machine_id} onChange={e => set("machine_id", e.target.value)} style={inputStyle}>
                {machines.map(m => <option key={m.machine_id} value={m.machine_id}>{m.machine_id}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{getLang().hr?.shiftsUi?.date}</label>
              <input type="date" value={form.shift_date} onChange={e => set("shift_date", e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{getLang().hr?.shiftsUi?.checkInTime}</label>
              <input type="time" value={form.start_time} onChange={e => set("start_time", e.target.value)} style={inputStyle} />
            </div>
          </div>
          {error && <div style={{ padding: "10px 14px", background: t.accent.redBg,
            border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8,
            border: `1px solid ${t.border.input}`, background: "none",
            color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{getLang().hr?.shiftsUi?.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{
            padding: "10px 24px", borderRadius: 8, border: "none",
            background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)",
            color: "#fff", cursor: loading ? "default" : "pointer",
            fontSize: 13, fontWeight: 600, boxShadow: loading ? "none" : t.shadow.btn,
          }}>{loading ? getLang().hr?.shiftsUi?.saving : getLang().hr?.shiftsUi?.assignShift}</button>
        </div>
      </div>
    </div>
  );
};

export default function Shifts() {
  const { t, themeName } = useTheme();
  const [mainTab, setMainTab] = useState(localStorage.getItem("tab_shifts") || "weekly");
  const [assignments, setAssignments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [weekStart, setWeekStart] = useState(today());
  const [stats, setStats] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API}/shifts/assignments`, { headers: headers() }),
        fetch(`${API}/attendance`, { headers: headers() }),
        fetch(`${API}/personnel`, { headers: headers() }),
        fetch(`${API}/machines/list`, { headers: headers() }),
      ]);
      if (r1.ok) {
        const d = await r1.json();
        const list = d.assignments || [];
        setAssignments(list);
        setStats({
          total: list.length,
          planned: list.filter(a => a.status === "planned").length,
          completed: list.filter(a => a.status === "completed").length,
        });
      }
      if (r2.ok) { const d = await r2.json(); setAttendance(d.attendance || []); }
      if (r3.ok) { const d = await r3.json(); setPersonnel(d.personnel || []); }
      if (r4.ok) { const d = await r4.json(); setMachines(d.machines || []); }
    } catch (e) { console.log(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const handleDelete = async (id) => {
    try {
      await fetch(`${API}/shifts/assignments/${id}`, { method: "DELETE", headers: headers() });
      fetchData();
    } catch {}
  };


  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary,
      fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@keyframes sf-spin { to{transform:rotate(360deg)} }`}</style>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>
              📅 {getLang().hr?.shiftsUi?.title}
            </h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {getLang().hr?.shiftsUi?.subtitle}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10,
              border: `1px solid ${t.border.input}`, background: t.bg.toggle,
              color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄 {getLang().hr?.shiftsUi?.refresh}</button>
            <button onClick={() => setShowAssign(true)} style={{
              padding: "10px 20px", borderRadius: 10, border: "none",
              background: "linear-gradient(135deg,#3b82f6,#6366f1)",
              color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: t.shadow.btn,
            }}>+ {getLang().hr?.shiftsUi?.assignShift}</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: getLang().hr?.shiftsUi?.totalAssignments, value: stats.total || 0, color: t.accent.blue, icon: "📅" },
            { label: getLang().hr?.shiftsUi?.planned, value: stats.planned || 0, color: t.accent.yellow, icon: "⏳" },
            { label: getLang().hr?.shiftsUi?.completed, value: stats.completed || 0, color: t.accent.green, icon: "✅" },
            { label: getLang().hr?.shiftsUi?.activePersonnel, value: personnel.length, color: t.accent.purple, icon: "👥" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, minWidth: 130, background: t.bg.card,
              border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 12, boxShadow: t.shadow.card }}>
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 4, marginBottom: 20,
          background: t.bg.toggle, border: `1px solid ${t.border.input}`,
          borderRadius: 12, padding: 4, width: "fit-content" }}>
          {[
            ["weekly", `📅 ${getLang().hr?.shiftsUi?.weeklyCalendar}`],
            ["list", `📋 ${getLang().hr?.shiftsUi?.shiftList}`],
            ["attendance", `✅ ${getLang().hr?.shiftsUi?.attendanceTracking}`],
          ].map(([v, label]) => (
            <button key={v} onClick={() => { setMainTab(v); localStorage.setItem("tab_shifts", v); }} style={{
              padding: "9px 18px", borderRadius: 9, border: "none", cursor: "pointer",
              background: mainTab === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none",
              color: mainTab === v ? t.text.primary : t.text.muted,
              fontSize: 13, fontWeight: 600,
              boxShadow: mainTab === v ? t.shadow.card : "none", transition: "all 0.2s",
            }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`,
              borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>Yükleniyor...</span>
          </div>
        ) : (
          <>
            {mainTab === "weekly" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button onClick={() => setWeekStart(addDays(weekStart, -7))} style={{
                    padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                    border: `1px solid ${t.border.input}`, background: t.bg.toggle,
                    color: t.text.secondary, fontSize: 14 }}>← {getLang().hr?.shiftsUi?.previous}</button>
                  <span style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>
                    {fmt(weekStart)} — {fmt(addDays(weekStart, 6))}
                  </span>
                  <button onClick={() => setWeekStart(addDays(weekStart, 7))} style={{
                    padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                    border: `1px solid ${t.border.input}`, background: t.bg.toggle,
                    color: t.text.secondary, fontSize: 14 }}>{getLang().hr?.shiftsUi?.next} →</button>
                  <button onClick={() => setWeekStart(today())} style={{
                    padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                    border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg,
                    color: t.accent.blue, fontSize: 12, fontWeight: 600 }}>{getLang().hr?.shiftsUi?.today}</button>
                </div>

                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 16, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <div style={{ display: "grid", gridTemplateColumns: "140px repeat(7,1fr)",
                    borderBottom: `1px solid ${t.border.default}`, background: t.bg.theadRow }}>
                    <div style={{ padding: "12px 16px", fontSize: 11, fontWeight: 700,
                      color: t.text.label, textTransform: "uppercase" }}>{getLang().hr?.shiftsUi?.personnel}</div>
                    {weekDays.map(day => {
                      const isToday = day === today();
                      return (
                        <div key={day} style={{ padding: "12px 8px", textAlign: "center",
                          borderLeft: `1px solid ${t.border.subtle}` }}>
                          <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 600 }}>{dayName(day)}</div>
                          <div style={{ fontSize: 14, fontWeight: 800,
                            color: isToday ? t.accent.blue : t.text.primary,
                            background: isToday ? t.accent.blueBg : "none",
                            borderRadius: "50%", width: 28, height: 28,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            margin: "2px auto 0" }}>
                            {new Date(day).getDate()}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {personnel.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: t.text.placeholder }}>
                      {getLang().hr?.shiftsUi?.noPersonnel}
                    </div>
                  ) : personnel.map((p, pi) => (
                    <div key={p.id} style={{ display: "grid", gridTemplateColumns: "140px repeat(7,1fr)",
                      borderTop: pi > 0 ? `1px solid ${t.border.subtle}` : "none",
                      background: pi % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                      <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                          background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 700, color: "#fff" }}>
                          {(p.name||"?")[0]}{(p.surname||"")[0]}
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: t.text.primary }}>
                            {p.name} {p.surname}
                          </div>
                          <div style={{ fontSize: 10, color: t.text.muted }}>{p.role}</div>
                        </div>
                      </div>
                      {weekDays.map(day => {
                        const dayAssignments = assignments.filter(a =>
                          String(a.personnel_id) === String(p.id) &&
                          (a.shift_date || "").slice(0, 10) === day
                        );
                        return (
                          <div key={day} style={{ padding: "4px", minHeight: 52,
                            borderLeft: `1px solid ${t.border.subtle}`,
                            display: "flex", flexDirection: "column", gap: 2 }}>
                            {dayAssignments.map((a, ai) => {
                              const cfg = SHIFT_CONFIG[a.shift_name] || SHIFT_CONFIG["Gunduz"];
                              return (
                                <div key={ai} style={{ background: cfg.bg,
                                  border: `1px solid ${cfg.color}44`,
                                  borderRadius: 6, padding: "3px 6px",
                                  display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <div>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: cfg.color }}>
                                      {cfg.icon} {getShiftConfigLabel(a.shift_name)}
                                    </div>
                                    <div style={{ fontSize: 9, color: t.text.muted }}>{a.machine_id}</div>
                                  </div>
                                  <button onClick={() => handleDelete(a.id)} style={{
                                    background: "none", border: "none", cursor: "pointer",
                                    color: t.text.muted, fontSize: 10, padding: "0 2px" }}>✕</button>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {Object.entries(SHIFT_CONFIG).map(([key, cfg]) => (
                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: t.text.muted }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: cfg.bg, border: `1px solid ${cfg.color}` }} />
                      {cfg.icon} {getShiftConfigLabel(key)} ({cfg.hours})
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mainTab === "list" && (
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: t.bg.theadRow }}>
                      {[
                        getLang().hr?.shiftsUi?.personnel,
                        getLang().hr?.shiftsUi?.role,
                        getLang().hr?.shiftsUi?.machine,
                        getLang().hr?.shiftsUi?.shift,
                        getLang().hr?.shiftsUi?.date,
                        getLang().hr?.shiftsUi?.checkIn,
                        getLang().hr?.shiftsUi?.checkOut,
                        getLang().hr?.shiftsUi?.status,
                        getLang().hr?.shiftsUi?.action,
                      ].map(h => (
                        <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11,
                          fontWeight: 700, color: t.text.label, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.length === 0 ? (
                      <tr><td colSpan={9} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>
                        {getLang().hr?.shiftsUi?.noAssignments}
                      </td></tr>
                    ) : assignments.map((a, i) => {
                      const cfg = SHIFT_CONFIG[a.shift_name] || SHIFT_CONFIG["Gunduz"];
                      const isToday = (a.shift_date || "").slice(0, 10) === today();
                      return (
                        <tr key={a.id || i} style={{ borderTop: `1px solid ${t.border.subtle}`,
                          background: isToday ? `${t.accent.blue}08` : i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                          <td style={{ padding: "12px 14px", fontWeight: 600, color: t.text.primary, fontSize: 13 }}>
                            {a.personel}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{getRoleLabel(a.role)}</td>
                          <td style={{ padding: "12px 14px", fontSize: 13, color: t.accent.blue, fontWeight: 600 }}>
                            {a.machine_id}
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px",
                              borderRadius: 20, color: cfg.color, background: cfg.bg,
                              border: `1px solid ${cfg.color}44` }}>
                              {cfg.icon} {getShiftConfigLabel(a.shift_name)}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>
                            {fmt(a.shift_date)}
                            {isToday && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700,
                              color: t.accent.blue, background: t.accent.blueBg,
                              padding: "1px 6px", borderRadius: 20 }}>{getLang().hr?.shiftsUi?.today}</span>}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.secondary }}>{a.start_time || "—"}</td>
                          <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.secondary }}>{a.end_time || "—"}</td>
                          <td style={{ padding: "12px 14px" }}>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                              color: a.status === "completed" ? t.accent.green : t.accent.yellow,
                              background: a.status === "completed" ? t.accent.greenBg : t.accent.yellowBg }}>
                              {a.status === "completed"
                            ? getLang().hr?.shiftsUi?.statusCompleted
                            : getLang().hr?.shiftsUi?.statusPlanned}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <button onClick={() => handleDelete(a.id)} style={{
                              padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                              background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, color: t.accent.red,
                            }}>{getLang().hr?.shiftsUi?.delete}</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {mainTab === "attendance" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
                  {Object.entries(ATTEND_STATUS).map(([k, v]) => {
                    const count = attendance.filter(a => a.status === k).length;
                    return (
                      <div key={k} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                        borderRadius: 12, padding: "14px 16px", boxShadow: t.shadow.card }}>
                        <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 4,
                          textTransform: "uppercase", fontWeight: 600 }}>{getAttendStatusLabel(k)}</div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: v.color }}>{count}</div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: t.bg.theadRow }}>
                        {[
                          getLang().hr?.shiftsUi?.personnel,
                          getLang().hr?.shiftsUi?.role,
                          getLang().hr?.shiftsUi?.date,
                          getLang().hr?.shiftsUi?.checkIn,
                          getLang().hr?.shiftsUi?.checkOut,
                          getLang().hr?.shiftsUi?.workedHours,
                          getLang().hr?.shiftsUi?.overtime,
                          getLang().hr?.shiftsUi?.status,
                        ].map(h => (
                          <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11,
                            fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.length === 0 ? (
                        <tr><td colSpan={8} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>
                          {getLang().hr?.shiftsUi?.noAttendance}
                        </td></tr>
                      ) : attendance.slice(0, 50).map((a, i) => {
                        const sc = ATTEND_STATUS[a.status] || ATTEND_STATUS.present;
                        return (
                          <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}`,
                            background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                            <td style={{ padding: "11px 14px", fontWeight: 600, color: t.text.primary, fontSize: 13 }}>{a.personel}</td>
                            <td style={{ padding: "11px 14px", fontSize: 12, color: t.text.muted }}>{getRoleLabel(a.role)}</td>
                            <td style={{ padding: "11px 14px", fontSize: 12, color: t.text.muted }}>{fmt(a.work_date)}</td>
                            <td style={{ padding: "11px 14px", fontSize: 13, color: t.text.secondary }}>{a.check_in || "—"}</td>
                            <td style={{ padding: "11px 14px", fontSize: 13, color: t.text.secondary }}>{a.check_out || "—"}</td>
                            <td style={{ padding: "11px 14px", fontWeight: 700, color: t.accent.blue, fontSize: 13 }}>
                              {a.worked_hours ? parseFloat(a.worked_hours).toFixed(1) : "—"}
                            </td>
                            <td style={{ padding: "11px 14px", fontWeight: 600, fontSize: 13,
                              color: parseFloat(a.overtime_hours) > 0 ? t.accent.yellow : t.text.muted }}>
                              {a.overtime_hours ? parseFloat(a.overtime_hours).toFixed(1) : "0"}
                            </td>
                            <td style={{ padding: "11px 14px" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px",
                                borderRadius: 20, color: sc.color, background: `${sc.color}18` }}>
                                {getAttendStatusLabel(a.status)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showAssign && (
        <AssignModal personnel={personnel} machines={machines} t={t}
          onClose={() => setShowAssign(false)}
          onSaved={() => { setShowAssign(false); fetchData(); }} />
      )}
    </div>
  );
}
